import React, { useState, useEffect } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMapEvents, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';

// Fix for default Leaflet marker icons in React
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
    iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
    shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

function MapClickHandler({ onMapClick }) {
    useMapEvents({
        click(e) {
            onMapClick(e.latlng);
        },
    });
    return null;
}

function MapViewController({ center }) {
    const map = useMap();

    useEffect(() => {
        if (center && center.length === 2) {
            map.setView(center, map.getZoom());
        }
    }, [center, map]);

    return null;
}

export default function RouteStudio({ instructorId, learners = [] }) {
    const [routes, setRoutes] = useState([]);
    const [currentRouteName, setCurrentRouteName] = useState('');
    const [routeCategory, setRouteCategory] = useState('Examiner-style practice');
    const [routeDescription, setRouteDescription] = useState('');
    const [waypoints, setWaypoints] = useState([]);
    const [routeCoordinates, setRouteCoordinates] = useState([]); // Road-snapped path
    
    // View mode: 'builder' or 'library'
    const [activeTab, setActiveTab] = useState('builder');
    
    // Assignment modal state
    const [showAssignModal, setShowAssignModal] = useState(false);
    const [selectedRouteForAssign, setSelectedRouteForAssign] = useState(null);
    const [targetLearnerId, setTargetLearnerId] = useState(learners[0]?.id || '');

    // Search query state
    const [searchQuery, setSearchQuery] = useState('');
    const [mapCenter, setMapCenter] = useState([51.5074, -0.1278]); // Default to London

    const fetchRoutes = async () => {
        if (!instructorId) return;
        try {
            const res = await fetch(`/api/instructor-routes?instructorId=${instructorId}`);
            const data = await res.json();
            if (Array.isArray(data)) setRoutes(data);
        } catch (err) {
            console.error("Error fetching routes:", err);
        }
    };

    useEffect(() => {
        fetchRoutes();
    }, [instructorId]);

    // 🚗 Fetch road-snapped driving directions from OSRM whenever waypoints change
    useEffect(() => {
        const fetchRoadRoute = async () => {
            if (waypoints.length < 2) {
                setRouteCoordinates(waypoints.map(wp => [wp.lat, wp.lng]));
                return;
            }

            // Format coordinates as "{lng},{lat};{lng},{lat}..." for OSRM API
            const coordsString = waypoints.map(wp => `${wp.lng},${wp.lat}`).join(';');
            try {
                const res = await fetch(`https://router.project-osrm.org/route/v1/driving/${coordsString}?overview=full&geometries=geojson`);
                const data = await res.json();
                if (data.code === 'Ok' && data.routes && data.routes.length > 0) {
                    // OSRM returns coordinates as [lng, lat], Leaflet needs [lat, lng]
                    const latLngs = data.routes[0].geometry.coordinates.map(coord => [coord[1], coord[0]]);
                    setRouteCoordinates(latLngs);
                } else {
                    // Fallback to straight lines if routing fails
                    setRouteCoordinates(waypoints.map(wp => [wp.lat, wp.lng]));
                }
            } catch (err) {
                console.error("OSRM Routing error:", err);
                setRouteCoordinates(waypoints.map(wp => [wp.lat, wp.lng]));
            }
        };

        fetchRoadRoute();
    }, [waypoints]);

    const handleMapClick = (latlng) => {
        const defaultName = `Stop ${waypoints.length + 1}`;
        setWaypoints(prev => [
            ...prev,
            { lat: latlng.lat, lng: latlng.lng, location_name: defaultName, notes: '' }
        ]);
    };

    const handleSearchLocation = async (e) => {
        e.preventDefault();
        if (!searchQuery.trim()) return;

        try {
            const res = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(searchQuery)}`);
            const data = await res.json();
            if (data && data.length > 0) {
                const { lat, lon } = data[0];
                setMapCenter([parseFloat(lat), parseFloat(lon)]);
            } else {
                alert("Location not found.");
            }
        } catch (err) {
            console.error("Geocoding search error:", err);
        }
    };

    const handleSaveRoute = async () => {
        if (!currentRouteName.trim()) {
            alert("Please provide a route name.");
            return;
        }
        if (waypoints.length === 0) {
            alert("Please click on the map to add at least one waypoint.");
            return;
        }

        try {
            const res = await fetch('/api/instructor-routes', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    instructor_id: instructorId,
                    name: currentRouteName,
                    category: routeCategory,
                    description: routeDescription,
                    waypoints: waypoints
                })
            });

            if (res.ok) {
                alert("🎉 Route successfully saved to Route Studio!");
                setCurrentRouteName('');
                setRouteDescription('');
                setWaypoints([]);
                fetchRoutes();
                setActiveTab('library');
            } else {
                alert("Failed to save route.");
            }
        } catch (err) {
            console.error("Save route error:", err);
        }
    };

    const handleDeleteRoute = async (routeId) => {
        if (!window.confirm("Are you sure you want to delete this saved route?")) return;
        try {
            const res = await fetch(`/api/instructor-routes/${routeId}`, { method: 'DELETE' });
            if (res.ok) {
                alert("🗑️ Route deleted successfully.");
                fetchRoutes();
            } else {
                alert("Failed to delete route.");
            }
        } catch (err) {
            console.error("Delete route error:", err);
        }
    };

    const handleLoadRouteOntoMap = (route) => {
        setCurrentRouteName(route.name);
        setRouteCategory(route.category || 'Examiner-style practice');
        setRouteDescription(route.description || '');
        setWaypoints(route.waypoints || []);
        setActiveTab('builder');
        if (route.waypoints && route.waypoints.length > 0) {
            setMapCenter([route.waypoints[0].lat, route.waypoints[0].lng]);
        }
    };

    const handleAssignRouteToStudent = async () => {
        if (!selectedRouteForAssign || !targetLearnerId) {
            alert("Please select a student.");
            return;
        }

        try {
            const res = await fetch('/api/route-assignments', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    route_id: selectedRouteForAssign.id,
                    learner_id: targetLearnerId
                })
            });

            if (res.ok) {
                alert(`✅ Route "${selectedRouteForAssign.name}" successfully assigned to student!`);
                setShowAssignModal(false);
                setSelectedRouteForAssign(null);
            } else {
                alert("Failed to assign route.");
            }
        } catch (err) {
            console.error("Assignment error:", err);
        }
    };

    return (
        <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '16px', padding: '10px 0 40px 0', color: '#fff', boxSizing: 'border-box' }}>
            
            {/* Header, Search & Tab Switcher */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px', background: '#162238', padding: '16px', borderRadius: '14px', border: '1px solid rgba(255,255,255,0.08)', boxSizing: 'border-box' }}>
                <div>
                    <h2 style={{ margin: 0, color: '#60a5fa', fontSize: '1.1rem', fontWeight: '800' }}>🗺️ LessonLane Route Studio</h2>
                    <p style={{ margin: '3px 0 0 0', color: '#94a3b8', fontSize: '0.75rem' }}>Click map to drop route stops, snap to real roads, and add notes.</p>
                </div>

                <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
                    <div style={{ display: 'flex', background: '#0f172a', padding: '3px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.1)' }}>
                        <button 
                            onClick={() => setActiveTab('builder')}
                            style={{ background: activeTab === 'builder' ? '#3b82f6' : 'transparent', color: '#fff', border: 'none', padding: '6px 12px', borderRadius: '6px', fontSize: '11px', fontWeight: 'bold', cursor: 'pointer' }}
                        >
                            ✏️ Builder
                        </button>
                        <button 
                            onClick={() => setActiveTab('library')}
                            style={{ background: activeTab === 'library' ? '#3b82f6' : 'transparent', color: '#fff', border: 'none', padding: '6px 12px', borderRadius: '6px', fontSize: '11px', fontWeight: 'bold', cursor: 'pointer' }}
                        >
                            📚 Saved Library ({routes.length})
                        </button>
                    </div>

                    <form onSubmit={handleSearchLocation} style={{ display: 'flex', gap: '6px' }}>
                        <input 
                            type="text" 
                            placeholder="Search postcode/town..." 
                            value={searchQuery}
                            onChange={e => setSearchQuery(e.target.value)}
                            style={{ padding: '7px 10px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.15)', background: '#0f172a', color: '#fff', fontSize: '11px', width: '160px', outline: 'none' }}
                        />
                        <button type="submit" style={{ background: '#3b82f6', color: '#fff', border: 'none', padding: '7px 10px', borderRadius: '8px', fontWeight: 'bold', fontSize: '11px', cursor: 'pointer' }}>
                            🔍
                        </button>
                    </form>
                </div>
            </div>

            {activeTab === 'builder' ? (
                <>
                    {/* Stacked Layout: Map Full Width on Top */}
                    <div style={{ width: '100%', borderRadius: '16px', overflow: 'hidden', border: '1px solid rgba(255,255,255,0.1)', height: '400px', position: 'relative', boxShadow: '0 8px 24px rgba(0,0,0,0.3)', boxSizing: 'border-box' }}>
                        <MapContainer center={mapCenter} zoom={13} style={{ width: '100%', height: '100%' }}>
                            <MapViewController center={mapCenter} />
                            <TileLayer
                                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                            />
                            <MapClickHandler onMapClick={handleMapClick} />
                            
                            {/* Road-Snapped Route Line */}
                            {routeCoordinates.length > 1 && (
                                <Polyline positions={routeCoordinates} color="#3b82f6" weight={5} opacity={0.85} />
                            )}

                            {waypoints.map((wp, idx) => (
                                <Marker key={idx} position={[wp.lat, wp.lng]}>
                                    <Popup>
                                        <b>Stop {idx + 1}: {wp.location_name || `Waypoint ${idx + 1}`}</b><br/>
                                        {wp.notes ? <p style={{ margin: '4px 0' }}><b>Notes:</b> {wp.notes}</p> : <i style={{ color: '#888' }}>No notes added</i>}
                                    </Popup>
                                </Marker>
                            ))}
                        </MapContainer>
                    </div>

                    {/* Route Builder Full Width Below */}
                    <div style={{ background: '#162238', borderRadius: '16px', border: '1px solid rgba(255,255,255,0.08)', padding: '18px', display: 'flex', flexDirection: 'column', gap: '14px', boxSizing: 'border-box', boxShadow: '0 8px 24px rgba(0,0,0,0.3)' }}>
                        <h3 style={{ margin: 0, fontSize: '1rem', color: '#60a5fa', fontWeight: '800' }}>📝 Route Builder</h3>

                        <div>
                            <label style={{ fontSize: '0.72rem', fontWeight: '800', color: '#94a3b8', display: 'block', marginBottom: '5px' }}>ROUTE NAME:</label>
                            <input 
                                type="text" 
                                placeholder="e.g. Test Centre Complex Roundabouts" 
                                value={currentRouteName} 
                                onChange={e => setCurrentRouteName(e.target.value)}
                                style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.15)', background: '#0f172a', color: '#fff', fontSize: '13px', boxSizing: 'border-box', outline: 'none' }}
                            />
                        </div>

                        <div>
                            <label style={{ fontSize: '0.72rem', fontWeight: '800', color: '#94a3b8', display: 'block', marginBottom: '5px' }}>CATEGORY:</label>
                            <select 
                                value={routeCategory} 
                                onChange={e => setRouteCategory(e.target.value)}
                                style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.15)', background: '#0f172a', color: '#fff', fontSize: '13px', boxSizing: 'border-box', outline: 'none' }}
                            >
                                <option value="Examiner-style practice">Examiner-style practice</option>
                                <option value="Roundabout training">Roundabout training</option>
                                <option value="Junction practice">Junction practice</option>
                                <option value="Manoeuvre practice">Manoeuvre practice</option>
                                <option value="Independent driving">Independent driving</option>
                                <option value="Theory & road-sign training">Theory & road-sign training</option>
                                <option value="Custom route">Custom route</option>
                            </select>
                        </div>

                        <div>
                            <label style={{ fontSize: '0.72rem', fontWeight: '800', color: '#94a3b8', display: 'block', marginBottom: '5px' }}>ROUTE DESCRIPTION:</label>
                            <textarea 
                                rows={2}
                                placeholder="Brief briefing summary for student..." 
                                value={routeDescription} 
                                onChange={e => setRouteDescription(e.target.value)}
                                style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.15)', background: '#0f172a', color: '#fff', fontSize: '13px', boxSizing: 'border-box', outline: 'none', resize: 'none' }}
                            />
                        </div>

                        <div>
                            <label style={{ fontSize: '0.72rem', fontWeight: '800', color: '#94a3b8', display: 'block', marginBottom: '6px' }}>WAYPOINTS ({waypoints.length}):</label>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', maxHeight: '200px', overflowY: 'auto' }}>
                                {waypoints.length === 0 ? (
                                    <p style={{ fontSize: '0.78rem', color: '#94a3b8', fontStyle: 'italic', margin: 0, padding: '6px 0' }}>Click anywhere on the map to drop numbered stops.</p>
                                ) : (
                                    waypoints.map((wp, idx) => (
                                        <div key={idx} style={{ background: '#0f172a', padding: '10px', borderRadius: '10px', border: '1px solid rgba(255,255,255,0.08)', display: 'flex', flexDirection: 'column', gap: '8px', boxSizing: 'border-box' }}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                <span style={{ fontSize: '0.78rem', color: '#60a5fa', fontWeight: 'bold' }}>Stop {idx + 1}</span>
                                                <button 
                                                    onClick={() => setWaypoints(prev => prev.filter((_, i) => i !== idx))}
                                                    style={{ background: 'transparent', border: 'none', color: '#fca5a5', cursor: 'pointer', fontSize: '13px', fontWeight: 'bold' }}
                                                >
                                                    ✕
                                                </button>
                                            </div>
                                            <input 
                                                type="text" 
                                                value={wp.location_name} 
                                                onChange={e => {
                                                    const val = e.target.value;
                                                    setWaypoints(prev => prev.map((item, i) => i === idx ? { ...item, location_name: val } : item));
                                                }}
                                                style={{ padding: '8px 10px', borderRadius: '7px', border: '1px solid rgba(255,255,255,0.12)', background: '#162238', color: '#fff', fontSize: '12px', boxSizing: 'border-box', width: '100%', outline: 'none' }}
                                            />
                                            <input 
                                                type="text" 
                                                placeholder="Hazard notes (e.g. check mirrors)..." 
                                                value={wp.notes} 
                                                onChange={e => {
                                                    const val = e.target.value;
                                                    setWaypoints(prev => prev.map((item, i) => i === idx ? { ...item, notes: val } : item));
                                                }}
                                                style={{ padding: '8px 10px', borderRadius: '7px', border: '1px solid rgba(255,255,255,0.12)', background: '#162238', color: '#fff', fontSize: '12px', boxSizing: 'border-box', width: '100%', outline: 'none' }}
                                            />
                                        </div>
                                    ))
                                )}
                            </div>
                        </div>

                        <button 
                            onClick={handleSaveRoute}
                            style={{ width: '100%', background: '#059669', color: '#fff', border: 'none', padding: '13px', borderRadius: '10px', fontWeight: 'bold', fontSize: '13px', cursor: 'pointer', boxShadow: '0 4px 12px rgba(5, 150, 105, 0.3)', boxSizing: 'border-box' }}
                        >
                            💾 Save Route to Library
                        </button>
                    </div>
                </>
            ) : (
                /* SAVED ROUTES LIBRARY VIEW */
                <div style={{ background: '#162238', borderRadius: '16px', border: '1px solid rgba(255,255,255,0.08)', padding: '20px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
                    <h3 style={{ margin: 0, color: '#60a5fa', fontSize: '1.05rem', fontWeight: '800' }}>📚 Saved Route Library ({routes.length})</h3>

                    {routes.length === 0 ? (
                        <div style={{ background: '#0f172a', padding: '30px', textAlign: 'center', borderRadius: '12px', color: '#94a3b8', fontSize: '0.85rem' }}>
                            No saved routes found. Switch to the <b>Builder</b> tab to create your first route!
                        </div>
                    ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                            {routes.map(r => (
                                <div key={r.id} style={{ background: '#0f172a', padding: '14px', borderRadius: '12px', border: '1px solid rgba(255,255,255,0.08)', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                        <div>
                                            <h4 style={{ margin: 0, color: '#fff', fontSize: '0.95rem', fontWeight: '800' }}>{r.name}</h4>
                                            <span style={{ fontSize: '0.7rem', background: 'rgba(59,130,246,0.15)', color: '#60a5fa', padding: '2px 8px', borderRadius: '6px', fontWeight: 'bold' }}>{r.category}</span>
                                        </div>
                                        <span style={{ fontSize: '0.7rem', color: '#94a3b8' }}>{r.waypoints?.length || 0} stops</span>
                                    </div>

                                    {r.description && (
                                        <p style={{ margin: 0, fontSize: '0.78rem', color: '#cbd5e1' }}>{r.description}</p>
                                    )}

                                    <div style={{ display: 'flex', gap: '8px', marginTop: '6px' }}>
                                        <button 
                                            onClick={() => handleLoadRouteOntoMap(r)}
                                            style={{ flex: 1, background: '#3b82f6', color: '#fff', border: 'none', padding: '8px', borderRadius: '8px', fontWeight: 'bold', fontSize: '11px', cursor: 'pointer' }}
                                        >
                                            🗺️ View / Edit
                                        </button>
                                        <button 
                                            onClick={() => {
                                                setSelectedRouteForAssign(r);
                                                setShowAssignModal(true);
                                            }}
                                            style={{ flex: 1, background: '#059669', color: '#fff', border: 'none', padding: '8px', borderRadius: '8px', fontWeight: 'bold', fontSize: '11px', cursor: 'pointer' }}
                                        >
                                            🎓 Assign to Student
                                        </button>
                                        <button 
                                            onClick={() => handleDeleteRoute(r.id)}
                                            style={{ background: 'rgba(239, 68, 68, 0.2)', color: '#fca5a5', border: 'none', padding: '8px 12px', borderRadius: '8px', cursor: 'pointer', fontSize: '12px' }}
                                            title="Delete Route"
                                        >
                                            🗑️
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* 🎓 Assign Route Modal */}
            {showAssignModal && selectedRouteForAssign && (
                <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }}>
                    <div style={{ background: '#162238', padding: '20px', borderRadius: '16px', width: '90%', maxWidth: '360px', border: '1.5px solid #3b82f6', color: '#fff' }}>
                        <h3 style={{ color: '#60a5fa', margin: '0 0 8px 0', fontSize: '1rem', fontWeight: '800' }}>Assign Route</h3>
                        <p style={{ fontSize: '0.78rem', color: '#94a3b8', margin: '0 0 14px 0' }}>Share <b>{selectedRouteForAssign.name}</b> directly to a student's portal:</p>

                        <div style={{ marginBottom: '16px' }}>
                            <label style={{ fontSize: '0.7rem', fontWeight: '800', color: '#94a3b8', display: 'block', marginBottom: '5px' }}>SELECT STUDENT:</label>
                            <select 
                                value={targetLearnerId} 
                                onChange={e => setTargetLearnerId(e.target.value)}
                                style={{ width: '100%', padding: '10px', borderRadius: '8px', background: '#0f172a', color: '#fff', border: '1px solid rgba(255,255,255,0.15)', fontSize: '12px' }}
                            >
                                {learners.map(l => (
                                    <option key={l.id} value={l.id}>{l.name} ({l.training_category || 'Car'})</option>
                                ))}
                            </select>
                        </div>

                        <button 
                            onClick={handleAssignRouteToStudent}
                            style={{ width: '100%', background: '#059669', color: '#fff', border: 'none', padding: '11px', borderRadius: '8px', fontWeight: 'bold', fontSize: '12px', cursor: 'pointer', marginBottom: '8px' }}
                        >
                            Confirm & Share to Portal 🚀
                        </button>
                        <button 
                            onClick={() => setShowAssignModal(false)}
                            style={{ width: '100%', background: '#334155', color: '#fff', border: 'none', padding: '9px', borderRadius: '8px', fontWeight: 'bold', fontSize: '12px', cursor: 'pointer' }}
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            )}

        </div>
    );
}